import React, { useEffect, useState } from "react";
import {
  useGetNotificationsByUserIdQuery,
  useAddNotificationMutation,
  useDeleteNotificationMutation,
  useUpdateNotificationMutation,
} from "../features/notifications/notificationsAPI";
import { useDispatch, useSelector } from "react-redux";
import {
  setAllNotifications,
  addNotification as addNotificationToSlice,
  deleteNotification as deleteNotificationFromSlice,
  updateNotification as updateNotificationInSlice,
  selectAllNotifications,
} from "../store/slices/notificationsSlice";
import { NotificationItem } from "./NotificationItem";
import Button from "./UI/Button";
import { selectCurrentUser } from "../store/slices/authSlice";
import {
  NotificationStatus,
  RelatedEntityType,
  Notification,
  NotificationType,
} from "@mekimix/shared/dist/types/Notification";


const NotificationList: React.FC = () => {
  const dispatch = useDispatch();
  const notifications = useSelector(selectAllNotifications);
  const [addNotification] = useAddNotificationMutation();
  const [deleteNotification] = useDeleteNotificationMutation();
  const [updateNotification] = useUpdateNotificationMutation();
  const [isAddOpen, setIsAddOpen] = useState(false);
  const [newContent, setNewContent] = useState("");
  const [newType, setNewType] = useState<NotificationType>(NotificationType.MEETING_REMINDER);
  const [newDateTime, setNewDateTime] = useState(new Date().toISOString().slice(0, 16));
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editContent, setEditContent] = useState("");
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const currentUser = useSelector(selectCurrentUser);
  const userId = currentUser?.id;
  const { data: apiNotifications, isLoading, isError } = useGetNotificationsByUserIdQuery(userId!, { skip: !userId });
  const [editDateTime, setEditDateTime] = useState("");
  const disabled = isAddOpen || editingId !== null;
  const [errorMessage, setErrorMessage] = useState("");

  useEffect(() => {
    if (apiNotifications) dispatch(setAllNotifications(apiNotifications));
  }, [apiNotifications, dispatch]);

  const resetForm = () => {
    setIsAddOpen(false);
    setNewContent("");
    setNewDateTime(new Date().toISOString().slice(0, 16));
    setErrorMessage("");
  };

  const getLocalDateTimeForInput = () => {
    const now = new Date();
    now.setMinutes(now.getMinutes() - now.getTimezoneOffset());
    return now.toISOString().slice(0, 16);
  };

  const handleAdd = async () => {
    if (!userId) return;
    if (!newContent.trim()) {
      setErrorMessage("התוכן לא יכול להיות ריק");
      return;
    }
    setErrorMessage("");

    try {
      const payload: Partial<Notification> = {
        user_id: userId,
        content: newContent,
        type: newType,
        date_time: new Date(newDateTime).toISOString() as unknown as Date,
        status: NotificationStatus.UNREAD,
        related_entity_id: "00000000-0000-0000-0000-000000000000",
        related_entity_type: RelatedEntityType.TASK,
      };
      const result = await addNotification(payload).unwrap();
      dispatch(addNotificationToSlice(result));
      resetForm();
    } catch (err) {
      alert("שגיאה בהוספת התראה: " + JSON.stringify(err));
    }
  };

  const confirmDelete = (id: string) => {
    setDeleteId(id);
    setShowDeleteConfirm(true);
  };

  const handleConfirmDelete = async () => {
    if (!deleteId) return;
    try {
      await deleteNotification(deleteId).unwrap();
      dispatch(deleteNotificationFromSlice(deleteId));
    } catch (err) {
      alert("שגיאה במחיקת התראה: " + JSON.stringify(err));
    } finally {
      setShowDeleteConfirm(false);
      setDeleteId(null);
    }
  };

  const handleUpdate = async () => {
    if (!editingId) return;

    if (!editContent.trim()) {
      setErrorMessage("התוכן לא יכול להיות ריק");
      return;
    }

    setErrorMessage("");
    try {
      const updated = await updateNotification({
        id: editingId,
        updates: {
          content: editContent,
          date_time: new Date(editDateTime),
        },
      }).unwrap();

      dispatch(updateNotificationInSlice(updated));
      cancelEditing();
    }
    catch (err) {
      alert("שגיאה בעדכון התראה: " + JSON.stringify(err));
    }
  };

  const startEditing = (id: string, content: string, dateTime: string) => {
    const date = new Date(dateTime);
    date.setMinutes(date.getMinutes() - date.getTimezoneOffset());
    setEditingId(id);
    setEditContent(content);
    setEditDateTime(date.toISOString().slice(0, 16));
  };


  const notificationTypeLabels: Record<NotificationType, string> = {
    [NotificationType.MEETING_REMINDER]: "תזכורת לפגישה",
    [NotificationType.TASK_REMINDER]: "תזכורת למשימה",
    [NotificationType.FAMILY_INACTIVE]: "משפחה לא פעילה",
  };


  const cancelEditing = () => {
    setEditingId(null);
    setEditContent("");
    setErrorMessage("");
  };

  if (!userId) return <div className="text-center py-4 text-red-600">משתמש לא מזוהה</div>;
  if (isLoading) return <div className="text-center py-4">טוען...</div>;
  if (isError) return <div className="text-center py-4">שגיאה בטעינה</div>;


  return (
    <div className="max-w-2xl mx-auto p-4 bg-white/50 rounded-lg shadow" dir="rtl">
      <h2 className="text-2xl font-bold mb-4 text-center">תזכורות</h2>

      <div className="flex justify-start mb-4">
        <Button
          onClick={() => {
            setIsAddOpen(true);
            setNewDateTime(getLocalDateTimeForInput());
          }}
          disabled={disabled}
        >
          הוסף תזכורת
        </Button>
      </div>

      {isAddOpen && (
        <div className="mb-6 p-4 rounded-lg bg-white/40 backdrop-blur-md border border-white/30 shadow-md">
          <div className="mb-3">
            <label className="block mb-1 font-semibold">תוכן:</label>
            <input
              type="text"
              value={newContent}
              onChange={(e) => setNewContent(e.target.value)}
              className="w-full border rounded p-2"
            />
          </div>

          <div className="mb-3">
            <label className="block mb-1 font-semibold">סוג:</label>
            <select
              value={newType}
              onChange={(e) => setNewType(e.target.value as NotificationType)}
              className="w-full border rounded p-2"
            >
              {Object.values(NotificationType).map((t) => (
                <option key={t} value={t}>
                  {notificationTypeLabels[t]}
                </option>
              ))}
            </select>
          </div>

          <div className="mb-3">
            <label className="block mb-1 font-semibold">תאריך ושעה:</label>
            <input
              type="datetime-local"
              value={newDateTime}
              onChange={(e) => setNewDateTime(e.target.value)}
              className="w-full border rounded p-2"
            />
            {errorMessage && (
              <div className="text-red-600 mt-1 text-sm">{errorMessage}</div>
            )}

          </div>

          <div className="flex gap-2">
            <Button onClick={handleAdd}>שמור</Button>
            <Button variant="outline" onClick={resetForm}>
              ביטול
            </Button>
          </div>
        </div>
      )}

      <div className="space-y-4">
        {notifications.length === 0 && <div className="text-center text-gray-500">אין תזכורות להצגה</div>}
        {notifications.map((n) =>
          editingId === n.id ? (
            <div key={n.id} className="p-4 border rounded bg-gray-50">
              <input
                type="text"
                value={editContent}
                onChange={(e) => setEditContent(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") handleUpdate();
                  if (e.key === "Escape") cancelEditing();
                }}
                className="w-full border rounded p-2"
              />
              <input
                type="datetime-local"
                value={editDateTime}
                onChange={(e) => setEditDateTime(e.target.value)}
                className="w-full border rounded p-2 mt-2" 
                />
                {errorMessage && (
                  <div className="text-red-600 mt-1 text-sm">{errorMessage}</div>
                )}
             
              <div className="flex justify-end gap-2 mt-2">
                <Button size="sm" onClick={handleUpdate}>
                  שמור
                </Button>
                <Button size="sm" variant="outline" onClick={cancelEditing}>
                  ביטול
                </Button>
              </div>
            </div>
          ) : (
            <NotificationItem
              key={n.id}
              id={n.id}
              content={n.content}
              date_time={new Date(n.date_time)}
              status={n.status}
              onDelete={() => confirmDelete(n.id)}
              onUpdate={() => startEditing(n.id, n.content, new Date(n.date_time).toISOString())}
              disabled={disabled}
              onUpdateStatus={async (id, status) => {
                try {
                  const updated = await updateNotification({
                    id,
                    updates: { status: status as NotificationStatus },
                  }).unwrap();
                  dispatch(updateNotificationInSlice(updated));
                } catch (err) {
                  alert("שגיאה בעדכון סטטוס: " + JSON.stringify(err));
                }
              }}
            />
          )
        )}
      </div>

      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-lg p-6 w-80 text-center">
            <p className="text-lg font-semibold mb-4">האם אתה בטוח שברצונך למחוק את ההתראה?</p>
            <div className="flex justify-center gap-4">
              <Button variant="danger" onClick={handleConfirmDelete}>
                כן
              </Button>
              <Button variant="outline" onClick={() => setShowDeleteConfirm(false)}>
                לא
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default NotificationList;
